
import axios from 'axios';

import {
  defineComponent,
  defineAsyncComponent,
  ref,
  onMounted,
  computed,
} from 'vue';

import { useRouter } from 'vue-router';

import type {
  EntriesPaginator,
  Endpoint,
  Endpoints,
} from '@/types';

import {
  TRAFFIC_TYPES,
  TRAFFIC_TYPES_MAP,
  TRAFFIC_CATEGORIES,
  TRAFFIC_CATEGORIES_MAP,
  REGIONS,
  REGIONS_MAP,
  PROTOCOLS_MAP,
  ENTRIES,
  PROJECT_STATUSES,
  ENDPOINT_STATUSES_MAP,
} from '@/libs/consts';

import useUser from '@/composable/useUser';

import useEntries from '@/composable/useEntries';

import useUrlState from '@/composable/useUrlState';

import useStatisticsLink from '@/composable/useStatisticsLink';

export default defineComponent({
  components: {
    EndpointMeta: defineAsyncComponent(() => import('@/components/EndpointMeta.vue')),
    UserColumn: defineAsyncComponent(() => import('@/components/UserColumn.vue')),
    SiteColumn: defineAsyncComponent(() => import('@/components/SiteColumn.vue')),
  },
  props: {
    filters: {
      type: Object,
      default: () => ({}),
    },
    showProjectColumn: {
      type: Boolean,
      default: true,
    },
    showUserColumn: {
      type: Boolean,
      default: true,
    },
  },
  setup(props) {
    const router = useRouter();

    const { activeUser: user } = useUser();

    const {
      entriesLoading: projectsLoading,
      entries: projects,
      fetchEntries: fetchProjects,
    } = useEntries();

    const { encodeStatisticsFilters } = useStatisticsLink();

    const endpoints = ref<Endpoints>(ENTRIES);

    const endpointsExpandedRows = ref([]);

    const endpointsFilters = useUrlState('filters', {
      project_id: null,
      user_id: null,
      status: null,
      uid: null,
      uuid: null,
      traffic_type: null,
      traffic_category: null,
      region: null,
    });

    const usersParams = computed(() => ({
      include: 'project',
      'filter[project_id]': user.isSuperAdmin() ? endpointsFilters.project_id : undefined,
    }));

    const fetchEndpoints = async (endpointsPaginator: EntriesPaginator): Promise<void> => {
      const response = await axios.post(
        '/api/endpoint/getEndpointsPaginator',
        {
          ...endpointsPaginator,
          ...endpointsFilters,
          ...props.filters,
        },
      );

      endpoints.value = response.data as Endpoints;
    };

    const onProjectChange = (): void => {
      endpointsFilters.user_id = null;
    };

    const addButtonHandler = async (): Promise<void> => {
      await router.push('/endpoints/add');
    };

    const editButtonHandler = async (endpoint: Endpoint): Promise<void> => {
      await router.push(`/endpoints/${endpoint.id}`);
    };

    onMounted(async (): Promise<void> => {
      if (user.isSuperAdmin()) {
        await fetchProjects('/api/project/getProjects');
      }
    });

    return {
      TRAFFIC_TYPES,
      TRAFFIC_TYPES_MAP,
      TRAFFIC_CATEGORIES,
      TRAFFIC_CATEGORIES_MAP,
      REGIONS,
      REGIONS_MAP,
      PROTOCOLS_MAP,
      PROJECT_STATUSES,
      ENDPOINT_STATUSES_MAP,
      user,
      projectsLoading,
      projects,
      encodeStatisticsFilters,
      endpoints,
      endpointsExpandedRows,
      endpointsFilters,
      usersParams,
      fetchEndpoints,
      onProjectChange,
      addButtonHandler: user.isAffiliate() ? null : addButtonHandler,
      editButtonHandler,
    };
  },
});
